import {
  ActionMenu,
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  ResourceTable,
  SectionCard,
  StatusBadge,
  TextInput,
  type Column,
} from '@verydia/ui';
import type { CustomerResource } from '@verydia/contracts';
import { VerydiaApiError } from '@verydia/sdk';
import { Archive, Building2, Pencil, Plus, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { describeFailure, isStarting } from '../lib/api.ts';
import {
  useArchiveCustomer,
  useCreateCustomer,
  useCustomers,
  useUpdateCustomer,
} from '../lib/identity-queries.ts';
import { CopyableId } from './copyable-id.tsx';

const isConflict = (error: unknown): boolean =>
  error instanceof VerydiaApiError && error.status === 409;

/** A reference the vendor did not supply is a dash, not an empty cell. */
function reference(value: string | null): string {
  return value ?? '—';
}

/* --------------------------------- create ---------------------------------- */

function CreateCustomerDialog({
  organizationId,
  open,
  onOpenChange,
}: {
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [externalReference, setExternalReference] = useState('');

  const create = useCreateCustomer(organizationId);
  const failure = create.isError ? describeFailure(create.error) : null;
  const referenceTaken = failure !== null && /already uses the reference/i.test(failure.message);

  const close = () => {
    create.reset();
    setName('');
    setEmail('');
    setExternalReference('');
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Add a customer"
      description="A company you sell to. Not a Verydia Control user, and not an end user of your software."
      footer={
        <>
          <Button onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<Plus />}
            disabled={name.trim() === '' || create.isPending}
            onClick={() =>
              create.mutate(
                {
                  name: name.trim(),
                  // Blank is absent, not empty. An empty reference would occupy
                  // the uniqueness slot and make the next blank one collide.
                  contact_email: email.trim() === '' ? null : email.trim(),
                  external_reference:
                    externalReference.trim() === '' ? null : externalReference.trim(),
                },
                { onSuccess: close },
              )
            }
          >
            {create.isPending ? 'Adding…' : 'Add customer'}
          </Button>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={(event) => event.preventDefault()}>
        <Field label="Customer name" required description="The company, as you refer to them.">
          {(props) => (
            <TextInput {...props} value={name} onChange={(event) => setName(event.target.value)} />
          )}
        </Field>

        {/*
          Optional, and genuinely so. A vendor importing from their CRM may have
          no single contact yet, and requiring one produces invented `noreply@`
          addresses that look real to everything downstream.
        */}
        <Field
          label="Contact email"
          description="Optional. A contact address — never used for sign-in, and never for marketing."
        >
          {(props) => (
            <TextInput
              {...props}
              type="email"
              placeholder="Leave blank if you do not have one"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Your reference"
          description="Optional. Your own key for this company — a CRM or billing id. Supplying one keeps a repeated import from creating a duplicate."
          error={referenceTaken ? failure.message : undefined}
        >
          {(props) => (
            <TextInput
              {...props}
              placeholder="ACME-001"
              value={externalReference}
              onChange={(event) => setExternalReference(event.target.value)}
            />
          )}
        </Field>

        {failure !== null && !referenceTaken ? (
          <p role="alert" className="text-[12px] text-[var(--vc-danger-text)]">
            {failure.message}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

/* ---------------------------------- edit ----------------------------------- */

function EditCustomerDialog({
  organizationId,
  customer,
  open,
  onOpenChange,
  onReload,
}: {
  organizationId: string;
  customer: CustomerResource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReload: () => void;
}) {
  const [name, setName] = useState(customer.name);
  const [email, setEmail] = useState(customer.contact_email ?? '');
  const [externalReference, setExternalReference] = useState(customer.external_reference ?? '');

  const update = useUpdateCustomer(organizationId, customer.id);
  const failure = update.isError ? describeFailure(update.error) : null;
  const conflict = isConflict(update.error);

  const close = () => {
    update.reset();
    setName(customer.name);
    setEmail(customer.contact_email ?? '');
    setExternalReference(customer.external_reference ?? '');
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={conflict ? 'This customer changed since you opened it' : `Edit ${customer.name}`}
      description={
        conflict
          ? 'Someone else saved a change while this page was open. Your edit was not applied, and theirs was kept.'
          : 'Changes are recorded in the audit log.'
      }
      footer={
        conflict ? (
          <>
            <Button onClick={close}>Discard my change</Button>
            <Button
              variant="primary"
              icon={<RotateCcw />}
              onClick={() => {
                update.reset();
                onReload();
              }}
            >
              Reload latest version
            </Button>
          </>
        ) : (
          <>
            <Button onClick={close} disabled={update.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={update.isPending || name.trim() === ''}
              onClick={() =>
                update.mutate(
                  {
                    expected_version: customer.version,
                    name: name.trim(),
                    contact_email: email.trim() === '' ? null : email.trim(),
                    external_reference:
                      externalReference.trim() === '' ? null : externalReference.trim(),
                  },
                  { onSuccess: close },
                )
              }
            >
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        )
      }
    >
      {conflict ? (
        <p className="text-[13px] text-[var(--vc-text-muted)]">
          Reload to see the current version, then reapply your change if it is still needed.
        </p>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={(event) => event.preventDefault()}>
          <Field label="Customer name" required>
            {(props) => (
              <TextInput
                {...props}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>
          <Field label="Contact email" description="Optional.">
            {(props) => (
              <TextInput
                {...props}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>
          <Field label="Your reference" description="Optional. Unique within your organization.">
            {(props) => (
              <TextInput
                {...props}
                value={externalReference}
                onChange={(event) => setExternalReference(event.target.value)}
              />
            )}
          </Field>

          {failure !== null ? (
            <p role="alert" className="text-[12px] text-[var(--vc-danger-text)]">
              {failure.message}
            </p>
          ) : null}
        </form>
      )}
    </Dialog>
  );
}

/* -------------------------------- archive ---------------------------------- */

function ArchiveCustomerDialog({
  organizationId,
  customer,
  open,
  onOpenChange,
}: {
  organizationId: string;
  customer: CustomerResource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const archive = useArchiveCustomer(organizationId, customer.id);
  const failure = archive.isError ? describeFailure(archive.error) : null;

  const close = () => {
    archive.reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={`Archive ${customer.name}?`}
      description="Archived customers are removed from normal selection. Historical records remain preserved."
      footer={
        <>
          <Button onClick={close} disabled={archive.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            icon={<Archive />}
            disabled={archive.isPending}
            onClick={() => archive.mutate(customer.version, { onSuccess: close })}
          >
            {archive.isPending ? 'Archiving…' : 'Archive customer'}
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-[var(--vc-text-muted)]">
        This customer keeps its identity and its history. Nothing is deleted — anything issued to
        them stays explicable.
      </p>
      {failure !== null ? (
        <p role="alert" className="mt-3 text-[12px] text-[var(--vc-danger-text)]">
          {failure.message}
        </p>
      ) : null}
    </Dialog>
  );
}

/* --------------------------------- panel ----------------------------------- */

export function CustomersPanel({ organizationId }: { organizationId: string | null }) {
  const customers = useCustomers(organizationId);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CustomerResource | null>(null);
  const [archiving, setArchiving] = useState<CustomerResource | null>(null);

  const rows = customers.data?.data.data ?? [];
  const failure = customers.isError ? describeFailure(customers.error) : null;

  const columns: ReadonlyArray<Column<CustomerResource>> = [
    {
      key: 'name',
      header: 'Customer',
      render: (row) => <span className="font-medium text-[var(--vc-text)]">{row.name}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <StatusBadge tone={row.status === 'ACTIVE' ? 'success' : 'neutral'}>
          {row.status === 'ACTIVE' ? 'Active' : 'Archived'}
        </StatusBadge>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (row) => (
        <span className="text-[var(--vc-text-muted)]">{row.contact_email ?? '—'}</span>
      ),
    },
    {
      key: 'reference',
      header: 'Your reference',
      render: (row) => (
        <span className="text-[var(--vc-text-muted)]">{reference(row.external_reference)}</span>
      ),
    },
    {
      key: 'id',
      header: 'Customer ID',
      render: (row) => <CopyableId value={row.id} label={`${row.name} customer id`} />,
    },
    {
      key: 'actions',
      header: '',
      // Pinned for the same reason the plans table pins it: at a narrow width
      // the actions are the first thing to scroll out of reach (defect 17).
      pin: 'right',
      render: (row) => (
        <ActionMenu
          label={`Actions for ${row.name}`}
          items={[
            {
              key: 'edit',
              label: 'Edit customer',
              icon: <Pencil />,
              onSelect: () => setEditing(row),
            },
            {
              key: 'archive',
              label: 'Archive customer',
              icon: <Archive />,
              destructive: true,
              onSelect: () => setArchiving(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <SectionCard
        title="Customers"
        description="The companies you license your software to."
        flush
        actions={
          organizationId === null ? undefined : (
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              Add customer
            </Button>
          )
        }
      >
        {customers.isPending ? (
          <LoadingState
            label={isStarting(customers.error) ? 'Waking the database' : 'Loading customers'}
          />
        ) : failure !== null ? (
          <ErrorState
            description={failure.message}
            requestId={failure.requestId}
            onRetry={() => void customers.refetch()}
          />
        ) : (
          <ResourceTable
            caption="Customers in this organization"
            columns={columns}
            rows={[...rows]}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                icon={<Building2 />}
                title="No customers yet"
                description="Add the companies you sell to. A customer is not a Verydia Control user and not an end user of your software — it is the legal entity that holds licences."
                action={
                  <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                    Add customer
                  </Button>
                }
              />
            }
          />
        )}
      </SectionCard>

      {organizationId !== null ? (
        <CreateCustomerDialog
          organizationId={organizationId}
          open={creating}
          onOpenChange={setCreating}
        />
      ) : null}

      {editing !== null && organizationId !== null ? (
        <EditCustomerDialog
          key={`edit-${editing.id}-${editing.version}`}
          organizationId={organizationId}
          customer={editing}
          open
          onOpenChange={(next) => (next ? undefined : setEditing(null))}
          onReload={() => {
            void customers.refetch();
            setEditing(null);
          }}
        />
      ) : null}

      {archiving !== null && organizationId !== null ? (
        <ArchiveCustomerDialog
          key={`archive-${archiving.id}-${archiving.version}`}
          organizationId={organizationId}
          customer={archiving}
          open
          onOpenChange={(next) => (next ? undefined : setArchiving(null))}
        />
      ) : null}
    </>
  );
}
